import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApprovalFlowDefinition, ApprovalStepDefinition, Department, Role, User } from '../models/approval-flow-definition.model';
import { ApprovalService } from '../services/approval.service';
import { DepartmentService } from '../services/department.service';
import { RoleService } from '../services/role.service';
import { UserService } from '../services/user.service';
import { AuthService, RoleOption } from '../../../../services/auth.service';
import { forkJoin } from 'rxjs'; // 添加 forkJoin 導入
import { TranslatePipe } from '../../../../pipes/translate.pipe';
import { environment } from '../../../../services/global-environment';
import { v4 as uuidv4 } from "uuid";
import { I18nService } from '../../../../services/i18n.service';
import { FormBuilderComponent } from '../../../form-builder/form-builder.component';
import { FormFieldConfig, DynamicFormComponent } from '../../../dynamic-form/dynamic-form/dynamic-form.component';


@Component({
  selector: 'app-approval-flow-management',
  templateUrl: './approval-flow-management.component.html',
  styleUrls: ['./approval-flow-management.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe, FormBuilderComponent, DynamicFormComponent]
})
export class ApprovalFlowManagementComponent implements OnInit {
  // 是否啟用 Mock 模式
  // 設為 false 時將使用實際 API 呼叫 (需同時設定 UserService 和 DepartmentService 的 useMock = false)
  private readonly isMockMode = false;

  // 流程清單
  flowDefinitions: ApprovalFlowDefinition[] = [];

  // 當前選中的流程
  selectedFlow: ApprovalFlowDefinition | null = null;

  // 新流程
  newFlow: ApprovalFlowDefinition = {
    name: '',
    description: '',
    formTypes: [],
    steps: [],
    companyid: '',
    assistantNo: '',
    status: 'active'
  };

  // 資料來源
  departments: Department[] = [];
  roles: Role[] = [];
  users: User[] = [];

  // UI狀態
  isCreatingNew = false;
  isEditingFlow = false;
  loading = false;
  error: string | null = null;

  // 臨時變數
  tempDepartmentId = '';
  tempRoleId = '';
  filteredRoles: Role[] = [];
  filteredUsers: User[] = [];

  constructor(
    private approvalService: ApprovalService,
    private departmentService: DepartmentService,
    private roleService: RoleService,
    private userService: UserService,
    private router: Router,
    private authService: AuthService,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {
    // 在這裡正確初始化 newFlow，此時 router 已經注入完成
    this.newFlow = this.getEmptyFlow();

    // 立即初始化角色過濾器，確保基礎角色選項可用
    this.initializeFilteredRoles();
      this.loadAllData();
  }



  loadFlowDefinitions(): void {
    this.loading = true;
    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      this.error = this.i18nService.translate('approval_flow_management.missing_company_id_or_assistant');
      this.loading = false;
      return;
    }

    this.approvalService.getApprovalFlowDefinitions(companyid, assistantNo).subscribe({
      next: (response) => {
        console.log("API response", response);

        // 處理新的資料格式：從 response.data 取得流程陣列
        if (response && response.success && response.data) {
          this.flowDefinitions = response.data;

          // 處理流程步驟的顯示名稱和向後相容性轉換
          this.processFlowStepNames(this.flowDefinitions);
          
          // 為每個流程的每個步驟進行向後相容性轉換
          this.flowDefinitions.forEach(flow => {
            flow.steps.forEach(step => {
              this.convertLegacyStepSettings(step);
            });
          });

          console.log("flows after processing", this.flowDefinitions);
        } else {
          this.flowDefinitions = [];
          console.warn('API 回應格式不正確或無資料');
        }

        this.loading = false;
      },
      error: (err) => {
        this.error = this.i18nService.translate('approval_flow_management.load_flow_definitions_failed');
        console.error('Error loading approval flow definitions', err);
        this.loading = false;
      }
    });
  }

  loadDepartments(): void {

    this.departmentService.getAllDepartments().subscribe({
      next: (departments) => {
        this.departments = departments;
        console.log('Departments loaded successfully:', departments.length, 'departments');
      },
      error: (err) => {
        console.error('Error loading departments', err);
        this.error = this.i18nService.translate('approval_flow_management.load_departments_failed');
      }
    });
  }

  loadRoles(): void {
    this.roleService.getAllRoles().subscribe({
      next: (roles) => {
        this.roles = roles;
        console.log('Roles loaded successfully:', roles.length, 'roles');
        // 角色載入完成後，初始化過濾的角色清單
        this.initializeFilteredRoles();
      },
      error: (err) => {
        console.error('Error loading roles', err);
        this.error = this.i18nService.translate('approval_flow_management.load_roles_failed');
      }
    });
  }

  loadUsers(): void {
    this.userService.getAllUsers().subscribe({
      next: (users) => {
        this.users = users;
        console.log('Users loaded successfully:', users.length, 'users');
      },
      error: (err) => {
        console.error('Error loading users', err);
        this.error = this.i18nService.translate('approval_flow_management.load_users_failed');
      }
    });
  }

  // 載入所有必要的資料，確保正確的順序
  loadAllData(): void {
    this.loading = true;

    // 首先載入部門、角色和使用者資料
    forkJoin({
      departments: this.departmentService.getAllDepartments(),
      roles: this.roleService.getAllRoles(),
      users: this.userService.getAllUsers()
    }).subscribe({
      next: (results) => {
        this.departments = results.departments;
        this.roles = results.roles;
        this.users = results.users;

        console.log('基礎資料載入完成:', {
          departments: this.departments.length,
          roles: this.roles.length,
          users: this.users.length
        });

        // 初始化角色過濾器
        this.initializeFilteredRoles();

        // 然後載入簽核流程定義
        this.loadFlowDefinitions();
      },
      error: (err) => {
        console.error('Error loading basic data', err);
        this.error = this.i18nService.translate('approval_flow_management.load_basic_data_failed');
        this.loading = false;
      }
    });
  }

  selectFlow(flow: ApprovalFlowDefinition): void {
    this.selectedFlow = { ...flow };
    // 確保選中的流程有正確的顯示名稱
    this.processFlowStepNames([this.selectedFlow]);
    this.isCreatingNew = false;
    this.isEditingFlow = false;
  }

  // 取得角色選項
  get roleOptions(): RoleOption[] {
    return this.authService.roleOptions;
  }

  // 初始化角色過濾器
  initializeFilteredRoles(): void {
    // 使用 authService 中定義的角色選項
    const allowedRoleValues = this.roleOptions.map(option => option.value);

    // 先從後端載入的角色中過濾
    let filteredFromBackend = this.roles.filter(role =>
      allowedRoleValues.includes(role.id)
    );

    // 如果後端沒有基礎角色資料，就建立虛擬的角色物件
    const missingRoles = allowedRoleValues.filter(roleValue =>
      !filteredFromBackend.some(role => role.id === roleValue)
    );

    const virtualRoles = missingRoles.map(roleValue => {
      const roleOption = this.roleOptions.find(option => option.value === roleValue);
      return {
        id: roleValue,
        name: roleOption?.label || roleValue,
        departmentId: undefined
      } as Role;
    });

    this.filteredRoles = [...filteredFromBackend, ...virtualRoles];

    console.log('初始化角色過濾器:', {
      allowedRoleValues,
      filteredFromBackend: filteredFromBackend.length,
      virtualRoles: virtualRoles.length,
      totalFilteredRoles: this.filteredRoles.length,
      filteredRoles: this.filteredRoles
    });
  }

  // 輔助方法：建立角色過濾清單（包含虛擬角色）
  private createFilteredRoles(includeBasicRoles: boolean, departmentId?: string): Role[] {
    const allowedRoleValues = this.roleOptions.map(option => option.value);
    let filtered: Role[] = [];

    if (includeBasicRoles) {
      // 先從後端載入的角色中過濾基本角色
      const basicRolesFromBackend = this.roles.filter(role =>
        allowedRoleValues.includes(role.id)
      );

      // 建立缺少的基本角色虛擬物件
      const missingBasicRoles = allowedRoleValues.filter(roleValue =>
        !basicRolesFromBackend.some(role => role.id === roleValue)
      );

      const virtualBasicRoles = missingBasicRoles.map(roleValue => {
        const roleOption = this.roleOptions.find(option => option.value === roleValue);
        return {
          id: roleValue,
          name: roleOption?.label || roleValue,
          departmentId: undefined
        } as Role;
      });

      filtered = [...basicRolesFromBackend, ...virtualBasicRoles];
    }

    // 如果指定了部門，則加入該部門的特定角色
    if (departmentId) {
      const departmentRoles = this.roles.filter(role =>
        role.departmentId === departmentId && !allowedRoleValues.includes(role.id)
      );
      filtered = [...filtered, ...departmentRoles];
    }

    return filtered;
  }

  startCreateFlow(): void {
    this.isCreatingNew = true;
    this.selectedFlow = null;
    this.newFlow = this.getEmptyFlow();
    // 初始化 filteredRoles 為所有角色
    this.initializeFilteredRoles();
  }

  startEditFlow(): void {
    if (!this.selectedFlow) return;
    this.isEditingFlow = true;
    // 初始化 filteredRoles 為所有角色
    this.initializeFilteredRoles();
  }

  cancelEdit(): void {
    if (this.isCreatingNew) {
      this.isCreatingNew = false;
    } else {
      this.isEditingFlow = false;
    }
  }

  saveFlow(): void {
    const flowToSave = this.isCreatingNew ? this.newFlow : this.selectedFlow;

    if (!flowToSave) return;

    // 驗證流程
    if (!flowToSave.name) {
      this.error = this.i18nService.translate('approval_flow_management.flow_name_required');
      return;
    }

    if (flowToSave.steps.length === 0) {
      this.error = this.i18nService.translate('approval_flow_management.at_least_one_step_required');
      return;
    }

    // 確保 companyid 和 assistantNo 存在
    if (!flowToSave.companyid) {
      flowToSave.companyid = environment.companyid || '';
    }
    if (!flowToSave.assistantNo) {
      flowToSave.assistantNo = this.router.routerState.snapshot.root.queryParams['id'];
    }

    if (!flowToSave.id) {
      flowToSave.id = uuidv4();
    }

    // 創建一個深度複製的流程物件，用於發送到 API
    const flowToSend = JSON.parse(JSON.stringify(flowToSave));

    // 清除 steps 中不必要的欄位並同步狀態
    flowToSend.steps = flowToSend.steps.map((step: any) => {
      const cleanStep = { ...step };
      delete cleanStep.roleName;
      delete cleanStep.departmentName;
      delete cleanStep.userName;
      
      // 確保從 customFormItems 同步 requireComment 和 requireSignature 狀態
      this.syncRequireSettingsFromCustomFormItems(cleanStep);
      
      return cleanStep;
    });

    this.loading = true;

    console.log('Saving flow (cleaned):', flowToSend);

    if (this.isCreatingNew) {
      this.approvalService.createApprovalFlowDefinition(flowToSend).subscribe({
        next: (createdFlow) => {
          // 處理新建立流程的顯示名稱
          this.processFlowStepNames([createdFlow]);

          this.flowDefinitions.push(createdFlow);
          this.loading = false;
          this.isCreatingNew = false;
          this.selectedFlow = createdFlow;
          this.error = null;

          console.log('Flow created and processed:', createdFlow);
        },
        error: (err) => {
          this.error = this.i18nService.translate('approval_flow_management.save_flow_failed');
          console.error('Error creating approval flow', err);
          this.loading = false;
        }
      });
    } else {
      this.approvalService.updateApprovalFlowDefinition(flowToSend).subscribe({
        next: (updatedFlow) => {
          // 處理更新流程的顯示名稱
          this.processFlowStepNames([updatedFlow]);

          const index = this.flowDefinitions.findIndex(f => f.id === updatedFlow.id);
          if (index !== -1) {
            this.flowDefinitions[index] = updatedFlow;
          }
          this.selectedFlow = updatedFlow;
          this.loading = false;
          this.isEditingFlow = false;
          this.error = null;

          console.log('Flow updated and processed:', updatedFlow);
        },
        error: (err) => {
          this.error = this.i18nService.translate('approval_flow_management.update_flow_failed');
          console.error('Error updating approval flow', err);
          this.loading = false;
        }
      });
    }
  }

  deleteFlow(flow: ApprovalFlowDefinition): void {
    if (!flow.id) return;

    // 提示使用者輸入流程名稱來確認刪除
    const userInput = prompt(
      `為了安全起見，請輸入簽核流程的名稱來確認刪除：\n\n流程名稱：「${flow.name}」\n\n請在下方輸入完整的流程名稱：`
    );

    // 檢查使用者是否取消或輸入為空
    if (userInput === null || userInput.trim() === '') {
      return;
    }

    // 檢查輸入的名稱是否與流程名稱完全匹配
    if (userInput.trim() !== flow.name.trim()) {
      alert('輸入的流程名稱不正確，刪除操作已取消。');
      return;
    }

    this.loading = true;

    // 取得必要的參數
    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      this.error = this.i18nService.translate('approval_flow_management.missing_company_id_or_assistant');
      this.loading = false;
      return;
    }

    this.approvalService.deleteApprovalFlowDefinition(flow.id, companyid, assistantNo).subscribe({
      next: () => {
        this.flowDefinitions = this.flowDefinitions.filter(f => f.id !== flow.id);
        if (this.selectedFlow && this.selectedFlow.id === flow.id) {
          this.selectedFlow = null;
        }
        this.loading = false;
        this.error = null;
        alert(`簽核流程「${flow.name}」已成功刪除。`);
      },
      error: (err) => {
        this.error = this.i18nService.translate('approval_flow_management.delete_flow_failed');
        console.error('Error deleting approval flow', err);
        this.loading = false;
      }
    });
  }

  addStep(): void {
    const flow = this.isCreatingNew ? this.newFlow : this.selectedFlow;
    if (!flow) return;

    const stepNumber = flow.steps.length + 1;
    const newStep: ApprovalStepDefinition = {
      id: uuidv4(), // Add a temporary ID for new steps
      name: `Step${stepNumber}`, // 自動產生預設步驟名稱
      order: stepNumber,
      approverType: 'role_based',
      optional: false,
      requireComment: true,
      requireSignature: true,
      customFormItems: [] // 初始化為空陣列
    };

    // 為新步驟進行向後相容性轉換（加入預設的意見和簽名元件）
    this.convertLegacyStepSettings(newStep);

    flow.steps.push(newStep);
  }

  removeStep(index: number): void {
    const flow = this.isCreatingNew ? this.newFlow : this.selectedFlow;
    if (!flow) return;

    flow.steps.splice(index, 1);

    // 重新排序並更新步驟名稱
    flow.steps.forEach((step, idx) => {
      step.order = idx + 1;
      // 如果步驟名稱是預設的格式，則更新為新的編號
      if (step.name && step.name.match(/^Step\d+$/)) {
        step.name = `Step${idx + 1}`;
      }
    });
  }

  moveStepUp(index: number): void {
    const flow = this.isCreatingNew ? this.newFlow : this.selectedFlow;
    if (!flow || index <= 0) return;

    const temp = flow.steps[index];
    flow.steps[index] = flow.steps[index - 1];
    flow.steps[index - 1] = temp;

    // 更新順序並重新編號預設步驟名稱
    flow.steps.forEach((step, idx) => {
      step.order = idx + 1;
      // 如果步驟名稱是預設的格式，則更新為新的編號
      if (step.name && step.name.match(/^Step\d+$/)) {
        step.name = `Step${idx + 1}`;
      }
    });
  }

  moveStepDown(index: number): void {
    const flow = this.isCreatingNew ? this.newFlow : this.selectedFlow;
    if (!flow || index >= flow.steps.length - 1) return;

    const temp = flow.steps[index];
    flow.steps[index] = flow.steps[index + 1];
    flow.steps[index + 1] = temp;

    // 更新順序並重新編號預設步驟名稱
    flow.steps.forEach((step, idx) => {
      step.order = idx + 1;
      // 如果步驟名稱是預設的格式，則更新為新的編號
      if (step.name && step.name.match(/^Step\d+$/)) {
        step.name = `Step${idx + 1}`;
      }
    });
  }

  // 部門變更處理
  onDepartmentChange(step: ApprovalStepDefinition, departmentId: string): void {
    step.departmentId = departmentId;

    // 處理特殊部門標識
    if (departmentId === 'submitter_dept') {
      step.departmentName = '同填寫人部門';
      step.isDynamicDepartment = true;
    }
    else if (departmentId === 'parent_dept') {
      step.departmentName = '填寫人的上級部門';
      step.isDynamicDepartment = true;
    }
    else if (departmentId) {
      step.departmentName = this.getDepartmentName(departmentId);
      step.isDynamicDepartment = false;
    } else {
      step.departmentName = '';
      step.isDynamicDepartment = false;
    }

    // 清除角色和用戶相關資訊
    step.roleId = '';
    step.roleName = '';

    // 篩選此部門的角色
    if (departmentId === 'submitter_dept' || departmentId === 'parent_dept') {
      // 對於動態部門，顯示 authService 中定義的基本角色類型
      this.filteredRoles = this.createFilteredRoles(true);
    } else if (departmentId) {
      // 對於固定部門，顯示該部門的特定角色和基本角色
      this.filteredRoles = this.createFilteredRoles(true, departmentId);
    } else {
      // 沒有選擇部門時，只顯示基本角色
      this.initializeFilteredRoles();
    }
  }

  onRoleChange(step: ApprovalStepDefinition, roleId: string): void {
    step.roleId = roleId;
    step.roleName = this.getRoleTranslatedName(roleId);

    // 清除用戶相關資訊
    step.userId = '';
    step.userName = '';
  }

  onUserChange(step: ApprovalStepDefinition, userId: string): void {
    step.userId = userId;
    step.userName = this.getUserName(userId);
  }

  onTypeChange(step: ApprovalStepDefinition): void {
    // 切換類型時清空不相關的欄位
    if (step.approverType === 'role_based') {
      step.userId = '';
      step.userName = '';
    } else {
      step.departmentId = '';
      step.departmentName = '';
      step.roleId = '';
      step.roleName = '';
    }
  }

  toggleFormType(type: string): void {
    const flow = this.isCreatingNew ? this.newFlow : this.selectedFlow;
    if (!flow) return;

    const index = flow.formTypes.indexOf(type);
    if (index === -1) {
      flow.formTypes.push(type);
    } else {
      flow.formTypes.splice(index, 1);
    }
  }

  isFormTypeSelected(type: string): boolean {
    const flow = this.isCreatingNew ? this.newFlow : this.selectedFlow;
    if (!flow) return false;

    return flow.formTypes.includes(type);
  }

  // 處理流程步驟的顯示名稱
  processFlowStepNames(flows: ApprovalFlowDefinition[]): void {
    flows.forEach(flow => {
      flow.steps.forEach(step => {
        // 處理動態部門名稱
        if (step.departmentId === 'submitter_dept') {
          step.departmentName = '同填寫人部門';
          step.isDynamicDepartment = true;
        } else if (step.departmentId === 'parent_dept') {
          step.departmentName = '填寫人的上級部門';
          step.isDynamicDepartment = true;
        } else if (step.departmentId && step.departmentId !== '') {
          // 對於固定部門，從部門清單中找到名稱
          const department = this.departments.find(d => d.id === step.departmentId);
          if (department) {
            step.departmentName = department.name;
            step.isDynamicDepartment = false;
          } else {
            // 如果找不到部門，保留原有名稱或設為空
            step.departmentName = step.departmentName || '未知部門';
            step.isDynamicDepartment = false;
          }
        }

        // 處理角色名稱 - 無論是否已有roleName都重新設定，確保一致性
        if (step.roleId) {
          step.roleName = this.getRoleTranslatedName(step.roleId);
        }

        // 處理使用者名稱 - 無論是否已有userName都重新設定，確保一致性
        if (step.userId) {
          const user = this.users.find(u => u.id === step.userId);
          if (user) {
            step.userName = user.name;
          } else if (!step.userName) {
            // 如果找不到使用者且沒有現有名稱，設為預設值
            step.userName = '未知使用者';
          }
        }
      });
    });
  }

  getEmptyFlow(): ApprovalFlowDefinition {
    const companyid = environment.companyid || '';
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    return {
      name: '',
      description: '',
      formTypes: [],
      steps: [],
      companyid: companyid,
      assistantNo: assistantNo,
      status: 'active'
    };
  }

  getDepartmentName(departmentId: string): string {
    if (!departmentId) return '';

    // 處理動態部門
    if (departmentId === 'submitter_dept') return '同填寫人部門';
    if (departmentId === 'parent_dept') return '填寫人的上級部門';

    // 處理固定部門
    const dept = this.departments.find(d => d.id === departmentId);
    return dept?.name || `未知部門(${departmentId})`;
  }

  // 新增：獲取部門顯示名稱（包含階層縮排）
  getDepartmentDisplayName(dept: Department): string {
    const prefix = '　'.repeat(dept.level || 0); // 全形空白字元用於縮排
    return prefix + dept.name;
  }

  getRoleName(roleId: string): string {
    return this.roles.find(r => r.id === roleId)?.name || '';
  }

  // 取得角色的翻譯名稱（支援多國語系）
  getRoleTranslatedName(roleId: string): string {
    if (!roleId) return '';

    // 先檢查是否為基礎角色，如果是則使用翻譯
    const roleOption = this.roleOptions.find(option => option.value === roleId);
    if (roleOption) {
      return roleOption.label;
    }

    // 如果不是基礎角色，則使用原本的名稱
    const role = this.roles.find(r => r.id === roleId);
    return role?.name || `未知角色(${roleId})`;
  }

  getUserName(userId: string): string {
    if (!userId) return '';

    const user = this.users.find(u => u.id === userId);
    return user?.name || `未知使用者(${userId})`;
  }

  getStepApproverInfo(step: ApprovalStepDefinition): string {
    if (step.approverType === 'role_based') {
      return `${step.departmentName || ''} - ${step.roleName || ''}`;
    } else {
      return step.userName || '';
    }
  }

  // Helper property to get the flow we're currently working with
  get currentFlow() {
    return this.isCreatingNew ? this.newFlow : (this.selectedFlow || this.getEmptyFlow());
  }

  // Methods for handling two-way bindings
  updateFlowName(value: string) {
    if (this.isCreatingNew) {
      this.newFlow.name = value;
    } else if (this.selectedFlow) {
      this.selectedFlow.name = value;
    }
  }

  updateFlowDescription(value: string) {
    if (this.isCreatingNew) {
      this.newFlow.description = value;
    } else if (this.selectedFlow) {
      this.selectedFlow.description = value;
    }
  }

  updateFlowStatus(value: 'active' | 'inactive') {
    if (this.isCreatingNew) {
      this.newFlow.status = value;
    } else if (this.selectedFlow) {
      this.selectedFlow.status = value;
    }
  }

  // Methods for handling TypeScript-safe event handling
  updateApproverType(step: any, type: string) {
    step.approverType = type;
    this.onTypeChange(step);
  }

  onDepartmentChangeSelect(step: any, event: Event) {
    const select = event.target as HTMLSelectElement;
    if (select) {
      this.onDepartmentChange(step, select.value);
    }
  }

  onRoleChangeSelect(step: any, event: Event) {
    const select = event.target as HTMLSelectElement;
    if (select) {
      this.onRoleChange(step, select.value);
    }
  }

  onUserChangeSelect(step: any, event: Event) {
    const select = event.target as HTMLSelectElement;
    if (select) {
      this.onUserChange(step, select.value);
    }
  }

  // 新增：進階設定相關屬性和方法
  
  // 簽核步驟模式允許的元件類型
  approvalStepAllowedComponents: string[] = [
    'multi-checkbox', 'radiobutton', 'file-upload-manager', 
    'textarea', 'date', 'fill-in', 'text-input', 'number-input', 'select', 'signature'
  ];

  // 當前正在編輯進階設定的步驟
  currentEditingStepIndex: number = -1;

  // 預覽相關屬性
  showPreviewModal: boolean = false;
  previewStepData: ApprovalStepDefinition | null = null;

  /**
   * 切換步驟的進階設定
   */
  toggleAdvancedSettings(step: ApprovalStepDefinition, stepIndex: number): void {
    step.hasAdvancedSettings = !step.hasAdvancedSettings;
    
    if (step.hasAdvancedSettings) {
      // 如果是首次開啟進階設定，初始化自訂表單項目
      if (!step.customFormItems) {
        step.customFormItems = [];
      }
      this.currentEditingStepIndex = stepIndex;
    } else {
      this.currentEditingStepIndex = -1;
    }
  }

  /**
   * 檢查是否正在編輯指定步驟
   */
  isEditingStep(step: ApprovalStepDefinition, stepIndex: number): boolean {
    return (step.hasAdvancedSettings === true) && this.currentEditingStepIndex === stepIndex;
  }

  /**
   * 步驟表單項目變更處理
   */
  onStepFormItemsChange(step: ApprovalStepDefinition, formItems: FormFieldConfig[]): void {
    step.customFormItems = [...formItems];
    console.log('步驟自訂表單更新:', step.name, formItems);
  }

  /**
   * 取得步驟的表單建構器項目（轉換格式）
   */
  getStepFormBuilderItems(step: ApprovalStepDefinition): any[] {
    if (!step.customFormItems) {
      return [];
    }
    
    return step.customFormItems.map((item, index) => ({
      ...item,
      id: item.id || `item_${index}`,
      selected: false
    }));
  }

  /**
   * 設定步驟的表單建構器項目
   */
  setStepFormBuilderItems(step: ApprovalStepDefinition, items: any[]): void {
    step.customFormItems = items.map(item => ({
      type: item.type,
      label: item.label,
      id: item.id,
      name: item.name,
      placeholder: item.placeholder,
      options: item.options,
      required: item.required,
      // 其他必要屬性
      content: item.content,
      textAlign: item.textAlign,
      format: item.format,
      multiSelect: item.multiSelect,
      _isSystemGenerated: item._isSystemGenerated, // 保持系統生成標記
      // 根據需要添加更多屬性
    }));
    
    // 同步 requireComment 和 requireSignature 狀態
    this.syncRequireSettingsFromCustomFormItems(step);
    
    // 強制觸發變更檢測以更新預覽區域
    console.log('更新步驟自訂表單:', step.name, '項目數量:', items.length);
  }

  /**
   * 檢查步驟是否有任何設定（用於顯示預覽區域）
   */
  hasStepSettings(step: ApprovalStepDefinition): boolean {
    return !!step.requireComment || 
           !!step.requireSignature || 
           !!(step.customFormItems && step.customFormItems.length > 0);
  }

  /**
   * 取得表單元件類型的顯示名稱
   */
  getFormItemTypeDisplay(type: string): string {
    const typeMap: { [key: string]: string } = {
      'multi-checkbox': '多選核取方塊',
      'radiobutton': '單選按鈕',
      'file-upload-manager': '檔案上傳',
      'textarea': '文字區塊',
      'date': '日期選擇',
      'fill-in': '填空',
      'text-input': '文字輸入',
      'number-input': '數字輸入',
      'select': '下拉選單',
      'text-display': '文字顯示',
      'image-display': '圖片顯示',
      'signature': '簽名'
    };
    return typeMap[type] || type;
  }

  /**
   * 預覽步驟表單
   */
  previewStepForm(step: ApprovalStepDefinition, stepIndex: number): void {
    if (!step.customFormItems || step.customFormItems.length === 0) {
      alert('此步驟尚未設定自訂表單元件');
      return;
    }

    this.previewStepData = { ...step };
    this.showPreviewModal = true;
    console.log('開啟預覽:', step.name, step.customFormItems);
  }

  /**
   * 關閉預覽模態
   */
  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewStepData = null;
  }

  /**
   * 處理 requireComment 變更：自動在 customFormItems 中加入或移除 textarea 元件
   */
  onRequireCommentChange(step: ApprovalStepDefinition, value: boolean): void {
    step.requireComment = value;
    
    if (!step.customFormItems) {
      step.customFormItems = [];
    }

    const commentItemId = 'approval_comment';
    const existingCommentIndex = step.customFormItems.findIndex(item => item.id === commentItemId);

    if (value) {
      // 需要填寫意見：加入 textarea 元件
      if (existingCommentIndex === -1) {
        const commentItem: FormFieldConfig = {
          type: 'textarea',
          label: this.i18nService.translate('approval.comment_label') || '簽核意見',
          id: commentItemId,
          name: commentItemId,
          placeholder: this.i18nService.translate('approval.comment_placeholder') || '請輸入簽核意見',
          required: true,
          _isSystemGenerated: true // 標記為系統生成的元件
        };
        step.customFormItems.unshift(commentItem); // 加到最前面
        console.log('已自動添加簽核意見元件到步驟:', step.name);
      }
    } else {
      // 不需要填寫意見：移除 textarea 元件（僅移除系統生成的）
      if (existingCommentIndex !== -1) {
        const existingItem = step.customFormItems[existingCommentIndex];
        if (existingItem && existingItem._isSystemGenerated) {
          step.customFormItems.splice(existingCommentIndex, 1);
          console.log('已自動移除系統生成的簽核意見元件從步驟:', step.name);
        }
      }
    }
  }

  /**
   * 處理 requireSignature 變更：自動在 customFormItems 中加入或移除 signature 元件
   */
  onRequireSignatureChange(step: ApprovalStepDefinition, value: boolean): void {
    step.requireSignature = value;
    
    if (!step.customFormItems) {
      step.customFormItems = [];
    }

    const signatureItemId = 'approval_signature';
    const existingSignatureIndex = step.customFormItems.findIndex(item => item.id === signatureItemId);

    if (value) {
      // 需要簽名：加入 signature 元件
      if (existingSignatureIndex === -1) {
        const signatureItem: FormFieldConfig = {
          type: 'signature',
          label: this.i18nService.translate('approval.signature_label') || '簽名',
          id: signatureItemId,
          name: signatureItemId,
          required: true,
          _isSystemGenerated: true // 標記為系統生成的元件
        };
        step.customFormItems.push(signatureItem); // 加到最後面
        console.log('已自動添加簽名元件到步驟:', step.name);
      }
    } else {
      // 不需要簽名：移除 signature 元件（僅移除系統生成的）
      if (existingSignatureIndex !== -1) {
        const existingItem = step.customFormItems[existingSignatureIndex];
        if (existingItem && existingItem._isSystemGenerated) {
          step.customFormItems.splice(existingSignatureIndex, 1);
          console.log('已自動移除系統生成的簽名元件從步驟:', step.name);
        }
      }
    }
  }

  /**
   * 從 customFormItems 推導 requireComment 和 requireSignature 的狀態
   * 用於向後相容性和資料同步
   */
  syncRequireSettingsFromCustomFormItems(step: ApprovalStepDefinition): void {
    if (!step.customFormItems) {
      step.requireComment = false;
      step.requireSignature = false;
      return;
    }

    // 檢查是否有簽核意見元件
    const hasCommentItem = step.customFormItems.some(item => 
      item.id === 'approval_comment' || item.type === 'textarea' && item.name === 'approval_comment'
    );
    
    // 檢查是否有簽名元件
    const hasSignatureItem = step.customFormItems.some(item => 
      item.id === 'approval_signature' || item.type === 'signature' && item.name === 'approval_signature'
    );

    step.requireComment = hasCommentItem;
    step.requireSignature = hasSignatureItem;
  }

  /**
   * 為現有步驟進行向後相容性轉換
   * 將 requireComment 和 requireSignature 轉換為對應的 customFormItems
   */
  convertLegacyStepSettings(step: ApprovalStepDefinition): void {
    if (!step.customFormItems) {
      step.customFormItems = [];
    }

    // 如果 requireComment 為 true 但沒有對應的 textarea 元件，則加入
    if (step.requireComment && !step.customFormItems.some(item => item.id === 'approval_comment')) {
      this.onRequireCommentChange(step, true);
    }

    // 如果 requireSignature 為 true 但沒有對應的 signature 元件，則加入
    if (step.requireSignature && !step.customFormItems.some(item => item.id === 'approval_signature')) {
      this.onRequireSignatureChange(step, true);
    }

    // 確保狀態同步
    this.syncRequireSettingsFromCustomFormItems(step);
  }

  /**
   * 處理步驟設定變更時的同步邏輯
   */
  onStepSettingsChange(step: ApprovalStepDefinition): void {
    // 從 customFormItems 同步狀態
    this.syncRequireSettingsFromCustomFormItems(step);
  }
}
